// The Translator's switch — a small dictionary icon beside the theme icon (the tab
// bar, desktop) and one Language row in the phone's menu drawer. Shown ONLY when a
// site admin switched the Translator on (Settings → Preferences); a site that never
// did draws nothing here.
//
//   <LanguageButton />     the tab bar's icon + its menu (a portal, like SiteSwitcher). The
//                          keyboard works it: Enter opens it on the ticked language, ↑ ↓ Home End
//                          move, Enter picks, Esc or Tab closes it and gives focus back.
//   <LanguageRows />       the drawer: "Language · Deutsch" — a tap shows the languages in place
//                          (the drawer's footer never grows by four rows)
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslator, chooseLanguage } from './store.js';
import { MAIN, languageOf } from './core.js';
import './LanguageButton.css';

export const BOOK_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
    <path d="m8 13 2.5-6 2.5 6" />
    <path d="M8.8 11h3.4" />
    <path d="M15 7h3M16.5 7c0 2.6-1 4.4-2.5 5.5M15 10.5c.8.9 1.8 1.6 3 2" />
  </svg>
);

// English (the main language) first, then the languages the admin offers
const choices = (t) => [MAIN, ...t.languages.map(languageOf).filter(Boolean)];
// what the person picked, when it is still on offer (an admin may have taken it off) — else English
const pickedOf = (t) => (t.languages.includes(t.choice) ? t.choice : 'en');

export default function LanguageButton() {
  const t = useTranslator();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const close = (refocus) => { setOpen(false); if (refocus && triggerRef.current) triggerRef.current.focus(); };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!rootRef.current?.contains(e.target) && !panelRef.current?.contains(e.target)) setOpen(false); };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', onResize);
    // focus the ticked language the moment the menu is there
    const id = setTimeout(() => { const on = panelRef.current?.querySelector('[aria-checked="true"]') || panelRef.current?.querySelector('[role="menuitemradio"]'); if (on) on.focus(); }, 0);
    return () => { clearTimeout(id); document.removeEventListener('mousedown', onDown); window.removeEventListener('resize', onResize); };
  }, [open]);

  if (!t.enabled) return null;
  const toggle = () => {
    if (open) { setOpen(false); return; }
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    setOpen(true);
  };
  const cur = languageOf(t.active) || MAIN;
  const pick = (code) => { chooseLanguage(code); close(true); };
  const onKey = (e) => {
    const items = [...(panelRef.current?.querySelectorAll('[role="menuitemradio"]') || [])];
    const i = items.indexOf(document.activeElement);
    const go = (k) => { if (items.length) items[(k + items.length) % items.length].focus(); };
    if (e.key === 'ArrowDown') { e.preventDefault(); go(i + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); go(i - 1); }
    else if (e.key === 'Home') { e.preventDefault(); go(0); }
    else if (e.key === 'End') { e.preventDefault(); go(items.length - 1); }
    else if (e.key === 'Escape') { e.preventDefault(); close(true); }
    else if (e.key === 'Tab') close(false);
  };
  return (
    <div className="tr-lang" ref={rootRef}>
      <button type="button" ref={triggerRef} className={`tab-theme-toggle tr-lang-toggle ${t.active !== 'en' ? 'is-on' : ''} ${open ? 'is-open' : ''}`}
        onClick={toggle} onKeyDown={(e) => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); toggle(); } }}
        aria-haspopup="menu" aria-expanded={open} aria-label={`Language: ${cur.native}`} title={`Language: ${cur.native}`}>
        {BOOK_ICON}
        {t.active !== 'en' && <span className="tr-lang-badge notranslate" aria-hidden="true">{t.active.toUpperCase()}</span>}
        {t.loading && <span className="tr-lang-busy" aria-hidden="true" />}
      </button>
      {open && pos && createPortal(
        <div ref={panelRef} className="tn-dropdown tr-lang-panel" role="menu" aria-label="Language" onKeyDown={onKey} style={{ top: pos.top, right: pos.right, left: 'auto' }}>
          <div className="tr-lang-heading" aria-hidden="true">Language</div>
          {choices(t).map((l) => {
            const on = pickedOf(t) === l.code;
            return (
              <button key={l.code} type="button" role="menuitemradio" aria-checked={on} tabIndex={on ? 0 : -1} className={`tr-lang-row ${on ? 'is-on' : ''}`} onClick={() => pick(l.code)}>
                <span className="tr-lang-code notranslate">{l.code.toUpperCase()}</span>
                <span className="tr-lang-name notranslate">{l.native}</span>
                {l.code === 'en' && <span className="tr-lang-main">main</span>}
                {on && <svg className="tr-lang-tick" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}

// the phone's menu drawer: ONE row ("Language · Deutsch"); a tap shows the languages in place
export function LanguageRows() {
  const t = useTranslator();
  const [open, setOpen] = useState(false);
  if (!t.enabled) return null;
  const cur = languageOf(pickedOf(t)) || MAIN;
  return (
    <>
      <button type="button" className="tn-drawer-row tr-drawer-head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="tn-drawer-row-label">Language · <span className="notranslate">{cur.native}</span></span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : 'none' }}><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {open && choices(t).map((l) => {
        const on = pickedOf(t) === l.code;
        return (
          <button key={l.code} type="button" className={`tn-drawer-row tr-drawer-lang ${on ? 'tn-drawer-row--current' : ''}`} aria-pressed={on}
            onClick={() => { chooseLanguage(l.code); setOpen(false); }}>
            <span className="tn-drawer-row-label notranslate">{l.native}{l.code === 'en' ? ' · main' : ''}</span>
          </button>
        );
      })}
    </>
  );
}
